import { Getter } from 'vuex-class'
import { Vue, Component, Ref, Model, Prop } from 'vue-property-decorator'
import { RootGetters } from '@/store/types'
import { userModule } from '@/store/namespaces'
import { UserGetters } from '@/store/modules/user/types'
import uniqCid from '@/helpers/uniqCid'
import isEmpty from '@/helpers/isEmpty'
import { setControlBarDataInUrl } from '../../lib/url-search-data'
import { setControlBarDataInStorage } from '../../lib/user-storage'
import type {
  SimpleFilterField as ISimpleFilterField,
  DatepickerFilterField as IDatepickerFilterField,
  SelectionFilterField as ISelectionFilterField,
  SimpleFilterFieldInstance as ISimpleFilterFieldInstance,
} from '../../types'
import { FiltersFieldSearchMode as EFiltersFieldSearchMode } from '../../types'
import SimpleFilterField from './ui/SimpleFilterField/SimpleFilterField.vue'
import DatepickerFilterField from './ui/DatepickerFilterField/DatepickerFilterField.vue'
import Menu from '@/components/Menu/Menu.vue'
import { ProjectID } from '@/types/platform'

@Component({
  components: {
    SimpleFilterField,
    DatepickerFilterField,
    Menu,
  },
})
export default class FiltersField extends Vue {
  @userModule.Getter
  private readonly getUserId!: UserGetters['getUserId']

  @Getter('getPlatform') private readonly platform!: RootGetters['getPlatform']

  @Model('change', { type: Array, required: true })
  private readonly value!: ISelectionFilterField[]

  @Ref('menu') private readonly menuRef?: {
    vMenuRef?: {
      updateDimensions: () => void
      $refs?: {
        content?: HTMLDivElement
      }
    }
  }

  @Ref('searchInput') private readonly searchInputRef?: HTMLInputElement

  @Ref('simpleFilterField')
  private readonly simpleFilterFieldRef?: ISimpleFilterFieldInstance

  @Prop({ type: Array, required: true }) private readonly items!: Array<
    ISimpleFilterField | IDatepickerFilterField
  >

  @Prop({ type: String })
  private readonly essenceStorage?: string

  @Prop({ type: Boolean, default: false }) private readonly saveInUrl!: boolean
  @Prop({ type: String, default: '' })
  private readonly searchInputValue!: string

  private handleCloseMenuClassName = `js-handle-close-menu-${uniqCid()}`
  private localSearchInputValue = ''
  private selectedFieldName: string | null = null
  private isFocusedSearchInput = false

  private menu = {
    isShowed: false,
    maxWidth: 0,
  }

  private get isOutlined() {
    return this.isFocusedSearchInput || this.menu.isShowed
  }

  private get computedMenuMaxHeight() {
    return this.selectedField?.type === 'datepicker' ? 430 : 300
  }

  private get computedMenuMaxWidth() {
    return this.selectedField?.type === 'datepicker' ? 320 : this.menu.maxWidth
  }

  private get searchMode(): EFiltersFieldSearchMode {
    if (this.selectedField) {
      return EFiltersFieldSearchMode.SELECT_FIELD_VALUE
    }

    if (!this.localSelections.length && !isEmpty(this.localSearchInputValue)) {
      return EFiltersFieldSearchMode.TEXT
    }

    return EFiltersFieldSearchMode.SELECT_FIELD
  }

  private get isTextSearchMode() {
    return this.searchMode === EFiltersFieldSearchMode.TEXT
  }

  private get isSelectFieldSearchMode() {
    return this.searchMode === EFiltersFieldSearchMode.SELECT_FIELD
  }

  private get isSelectFieldValueSearchMode() {
    return this.searchMode === EFiltersFieldSearchMode.SELECT_FIELD_VALUE
  }

  private get isClearable() {
    return (
      !isEmpty(this.localSearchInputValue) || !isEmpty(this.localSelections)
    )
  }

  private get isReadonlySearchInput() {
    if (
      this.selectedField?.type === 'datepicker' ||
      this.selectedField?.isMultiple
    ) {
      return true
    }

    return this.isSelectFieldSearchMode && !this.localFields.length
  }

  private get isDisabledSearchInput() {
    return this.isSelectFieldSearchMode && !this.localFields.length
  }

  private get menuContentClass() {
    const isHiddenMenu =
      this.isSelectFieldValueSearchMode &&
      this.selectedField?.type !== 'datepicker' &&
      !this.selectedField?.fetchItems &&
      !this.selectedField?.items?.length

    return [
      {
        value:
          'white rounded-0 filter-field-content-menu overscroll-behavior-contain',
      },
      {
        value: this.handleCloseMenuClassName,
      },
      {
        value: 'visibility-hidden',
        include:
          isHiddenMenu || this.isTextSearchMode || this.isDisabledSearchInput,
      },
    ]
      .reduce<string>((acc, { value, include = true }) => {
        if (include) {
          acc += ` ${value}`
        }

        return acc
      }, '')
      .trim()
  }

  private get fieldLabels() {
    return new Map(this.items.map(({ name, label }) => [name, label]))
  }

  private get localSelections() {
    const isMultipleSelectedField =
      this.selectedField?.type !== 'datepicker' &&
      this.selectedField?.isMultiple

    const fieldName = this.selectedField?.name
    const fieldLabel = this.selectedField?.label
    const fieldLabels = this.fieldLabels

    const selectionsMap = this.value.reduce<
      Map<
        string,
        Omit<ISelectionFilterField, 'value' | 'text'> & {
          label: string
          texts: string[]
        }
      >
    >((acc, item) => {
      const label = fieldLabels.get(item.field)

      if (!label) {
        return acc
      }

      let selection = acc.get(item.field)

      if (!selection) {
        selection = {
          field: item.field,
          label,
          texts: [],
        }

        acc.set(item.field, selection)
      }

      if (!isMultipleSelectedField && item.field === fieldName) {
        return acc
      }

      selection.texts.push(item.text)

      return acc
    }, new Map())

    if (fieldName && fieldLabel && !selectionsMap.has(fieldName)) {
      selectionsMap.set(fieldName, {
        field: fieldName,
        label: fieldLabel,
        texts: [],
      })
    }

    const activeSelection = fieldName ? selectionsMap.get(fieldName) : undefined

    if (activeSelection && fieldName) {
      selectionsMap.delete(fieldName)
      selectionsMap.set(fieldName, activeSelection)
    }

    return Array.from(selectionsMap.values())
  }

  private get localFields() {
    const localSelections = this.localSelections

    return this.items.filter(
      (item) => !localSelections.some(({ field }) => field === item.name)
    )
  }

  private get selectedField() {
    const selectedFieldName = this.selectedFieldName

    return this.items.find(({ name }) => name === selectedFieldName)
  }

  private onFocusSearchInput() {
    this.isFocusedSearchInput = true
  }

  private onBlurSearchInput() {
    this.isFocusedSearchInput = false
  }

  private onClickSearchInput() {
    this.toggleMenu(true)
  }

  private onChangeSearchInput() {
    this.emitChangeSearchInputValue(
      this.isTextSearchMode ? this.localSearchInputValue : ''
    )
  }

  private onEnterSearchInput() {
    this.autoSelectFilterFieldItem()
    this.submitFilters()
    this.toggleMenu(false)
    this.blurSearchInput()
  }

  private onSubmitFilters() {
    this.autoSelectFilterFieldItem()
    this.submitFilters()
    this.toggleMenu(false)
  }

  private autoSelectFilterFieldItem() {
    if (
      this.selectedField?.type !== 'datepicker' &&
      this.selectedField?.enterItem
    ) {
      this.selectedField.enterItem({
        items: this.simpleFilterFieldRef?.items ?? [],
        searchText: this.localSearchInputValue,
        filterFied: {
          name: this.selectedField.name,
          label: this.selectedField.label,
        },
        setItemForFilterField: this.setItemForFilterField,
      })
    } else if (
      this.selectedField?.name &&
      this.selectedField?.type !== 'datepicker' &&
      !this.selectedField?.isMultiple
    ) {
      this.simpleFilterFieldRef?.autoSelectFilterFieldItem()
    }
  }

  private focusSearchInput() {
    this.$nextTick(() => {
      this.searchInputRef?.focus()
      this.menuRef?.vMenuRef?.updateDimensions()
    })
  }

  private blurSearchInput() {
    this.$nextTick(() => {
      this.searchInputRef?.blur()
      this.menuRef?.vMenuRef?.updateDimensions()
    })
  }

  private scrollToTopMenu() {
    this.menuRef?.vMenuRef?.$refs?.content?.scrollTo(0, 0)
  }

  private toggleMenu(value: boolean) {
    this.menu.isShowed = value
  }

  private initMenu() {
    let searchInputResizeObserver: ResizeObserver | null = null

    if (this.searchInputRef) {
      searchInputResizeObserver = new ResizeObserver(([entry]) => {
        this.menu.maxWidth = entry.target.clientWidth
        this.menuRef?.vMenuRef?.updateDimensions()
      })

      searchInputResizeObserver.observe(this.searchInputRef)
    }

    const onBodyClick = (event: Event) => {
      const target = event.target as Element | null

      if (target?.closest(`.${this.handleCloseMenuClassName}`)) {
        return
      }

      this.toggleMenu(false)

      if (
        this.selectedFieldName &&
        this.value?.some(({ field }) => field === this.selectedFieldName)
      ) {
        this.selectedFieldName = null
        this.localSearchInputValue = ''
      }
    }

    document.body.addEventListener('mousedown', onBodyClick)
    document.body.addEventListener('touchstart', onBodyClick)

    this.$once('hook:beforeDestroy', () => {
      searchInputResizeObserver?.disconnect()
      document.body.removeEventListener('mousedown', onBodyClick)
      document.body.removeEventListener('touchstart', onBodyClick)
    })
  }

  private clearValue() {
    const newValue: ISelectionFilterField[] = []

    this.$emit('change:value', newValue)
    this.saveFiltersInUrl(newValue)
    this.saveFiltersInStorage(newValue)
    this.emitChangeSearchInputValue('')

    if (this.value.length || this.isTextSearchMode) {
      this.$emit('submit', this.value)
    }

    this.selectedFieldName = null
    this.localSearchInputValue = ''

    if (this.menu.isShowed) {
      this.focusSearchInput()
    }
  }

  private unselectField(fieldName: string) {
    this.toggleMenu(false)

    const hasSelectedFieldName = !!this.selectedFieldName

    if (this.selectedFieldName === fieldName) {
      this.selectedFieldName = null
    }

    this.localSearchInputValue = ''
    this.scrollToTopMenu()

    const filterField = this.value.find((item) => item.field === fieldName)

    if (filterField) {
      const newValue = this.value.filter((item) => item.field !== fieldName)

      this.saveFiltersInUrl(newValue)
      this.saveFiltersInStorage(newValue)
      this.$emit('change:value', newValue)
      this.submitFilters()
    }

    if (hasSelectedFieldName) {
      this.focusSearchInput()

      this.$nextTick(() => {
        this.toggleMenu(true)
      })
    }
  }

  private selectField(name: string) {
    this.toggleMenu(false)
    this.selectedFieldName = name

    this.focusSearchInput()
    this.scrollToTopMenu()

    this.$nextTick(() => {
      this.toggleMenu(true)
    })
  }

  private selectFieldBySelection(field: string) {
    const selection = this.localSelections.find(
      (selection) => selection.field === field
    )

    if (!selection) {
      return
    }

    if (selection.field === this.selectedFieldName) {
      this.focusSearchInput()

      return
    }

    const selectedItem = this.items.find(({ name }) => name === selection.field)

    if (selectedItem?.type === 'datepicker' || !selectedItem?.isMultiple) {
      this.localSearchInputValue = selection.texts[0] || ''
    } else {
      this.localSearchInputValue = ''
    }

    this.selectField(selection.field)
  }

  private setItemValueForFilterField({
    field,
  }: Pick<ISelectionFilterField, 'field' | 'value'>) {
    const text = this.items.find((item) => item.name === field)?.label

    if (isEmpty(text)) {
      return text
    }
  }

  private setItemForFilterField(
    item: ISelectionFilterField,
    autoSelect = false
  ) {
    if (!this.items.some(({ name }) => name === item.field)) {
      return
    }

    const isMultipleSelectedField =
      this.selectedField?.type !== 'datepicker' &&
      !!this.selectedField?.isMultiple

    const newValue = this.value.concat([]) || []

    const selectionIndex = newValue.findIndex(({ field, value }) => {
      if (isMultipleSelectedField) {
        return item.field === field && item.value === value
      }

      return item.field === field
    })

    const selectedItem =
      selectionIndex > -1 ? newValue[selectionIndex] : undefined

    if (selectionIndex === -1) {
      newValue.push(item)
    } else if (isMultipleSelectedField && selectedItem?.value === item.value) {
      newValue.splice(selectionIndex, 1)
    } else if (isMultipleSelectedField) {
      newValue.push(item)
    } else {
      newValue.splice(selectionIndex, 1, item)
    }

    if (isMultipleSelectedField) {
      this.toggleMenu(true)
    } else {
      this.selectedFieldName = null
      this.localSearchInputValue = ''
      this.scrollToTopMenu()

      if (!autoSelect) {
        this.toggleMenu(false)
      }
    }

    this.saveFiltersInUrl(newValue)
    this.saveFiltersInStorage(newValue)
    this.$emit('change:value', newValue)

    this.submitFilters()
  }

  private emitChangeSearchInputValue(value: string) {
    this.$emit('change:search-input-value', value)

    if (this.saveInUrl) {
      setControlBarDataInUrl({
        searchInputValue: value,
      })
    }

    if (this.essenceStorage) {
      setControlBarDataInStorage({
        searchInputValue: value,
        essence: this.essenceStorage,
        platform: this.platform,
        userId: this.getUserId(ProjectID[this.platform]),
      })
    }
  }

  private submitFilters() {
    this.$emit('submit', this.value)
  }

  private saveFiltersInStorage(value: ISelectionFilterField[]) {
    if (!this.essenceStorage) {
      return
    }

    setControlBarDataInStorage({
      essence: this.essenceStorage,
      filtersFieldValue: value,
      platform: this.platform,
      userId: this.getUserId(ProjectID[this.platform]),
    })
  }

  private saveFiltersInUrl(value: ISelectionFilterField[]) {
    if (!this.saveInUrl) {
      return
    }

    setControlBarDataInUrl({
      filterFields: value,
    })
  }

  private created() {
    this.$watch(
      () => {
        return this.searchInputValue
      },
      (searchInputValue) => {
        this.localSearchInputValue = searchInputValue
      },
      {
        immediate: true,
      }
    )
  }

  private mounted() {
    this.initMenu()
  }
}
